(() => {
  const MAX_SKU_TOKENS = 50;
  const REVIEWS_API_PER_PAGE = 100;
  /** Max reviews to fetch/render; show "500+" in UI when true total is higher. */
  const REVIEWS_FETCH_CAP = 500;
  /** Enough pages to fill the cap plus one short/empty tail (e.g. exactly 500 = 5×100 + probe). */
  const REVIEWS_API_MAX_PAGES = Math.ceil(REVIEWS_FETCH_CAP / REVIEWS_API_PER_PAGE) + 2;

  const summaryNodes = () => Array.from(document.querySelectorAll('[data-prl-rating]'));
  const reviewsNodes = () => Array.from(document.querySelectorAll('[data-prl-reviews]'));
  const cache = new Map();

  function buildKey(node) {
    return [
      node.dataset.prlStoreId,
      node.dataset.prlSkus
    ].join('|');
  }

  function normalizeSku(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[^a-z0-9_-]/g, '');
  }

  /** All variant / product identifiers from data-prl-skus (matches Polaris-style blob). */
  function getProductSkuTokens(node) {
    const raw = (node.dataset.prlSkus || '').split(';');
    const seen = new Set();
    const tokens = [];
    for (const part of raw) {
      const t = normalizeSku(part);
      if (!t || seen.has(t)) continue;
      seen.add(t);
      tokens.push(t);
      if (tokens.length >= MAX_SKU_TOKENS) break;
    }
    return tokens;
  }

  function buildReviewsApiUrl(node, page, perPage) {
    const url = new URL('https://api.reviews.io/reviews');
    const params = new URLSearchParams();
    params.set('store', node.dataset.prlStoreId || '');
    params.set('type', 'product_review');
    params.set('per_page', String(perPage));
    params.set('page', String(page));

    const tokens = getProductSkuTokens(node);
    if (tokens.length > 0) {
      params.set('sku', tokens.join(';'));
    }

    url.search = params.toString();
    return url.toString();
  }

  /**
   * Fetch all pages for this store/sku (API caps each page at per_page; widget totals need the full set).
   */
  async function getData(node) {
    const key = buildKey(node);
    if (!cache.has(key)) {
      cache.set(
        key,
        (async () => {
          if (getProductSkuTokens(node).length === 0) {
            return { reviews: [], data: [] };
          }

          const merged = [];
          let lastJson = null;
          let page = 1;
          let reportedTotal = 0;
          let reviewCountCapped = false;

          while (page <= REVIEWS_API_MAX_PAGES) {
            let response;
            try {
              response = await fetch(buildReviewsApiUrl(node, page, REVIEWS_API_PER_PAGE), {
                headers: { Accept: 'application/json' }
              });
            } catch (_net) {
              if (page === 1) throw _net;
              break;
            }
            if (!response.ok) {
              if (page === 1) throw new Error('Failed to fetch reviews');
              break;
            }
            let json;
            try {
              json = await response.json();
            } catch (_parse) {
              if (page === 1) throw _parse;
              break;
            }
            lastJson = json;
            const chunk = Array.isArray(json.reviews) ? json.reviews : [];
            const rc = Number(json.results_count || 0);
            const rv = Number(json.review_count || 0);
            const pageTotal = Math.max(rc, rv);
            if (pageTotal > reportedTotal) reportedTotal = pageTotal;

            merged.push(...chunk);
            if (merged.length > REVIEWS_FETCH_CAP) {
              merged.length = REVIEWS_FETCH_CAP;
              reviewCountCapped = true;
              break;
            }

            if (merged.length === REVIEWS_FETCH_CAP && reportedTotal > REVIEWS_FETCH_CAP) {
              reviewCountCapped = true;
              break;
            }

            if (chunk.length === 0) break;
            if (chunk.length < REVIEWS_API_PER_PAGE) break;
            page += 1;
            await new Promise((r) => setTimeout(r, 75));
          }

          if (!reviewCountCapped && reportedTotal > REVIEWS_FETCH_CAP) {
            reviewCountCapped = true;
          }

          return {
            reviews: merged,
            data: merged,
            average_rating: lastJson?.average_rating,
            results_count: reportedTotal || lastJson?.results_count,
            review_count: lastJson?.review_count,
            review_count_capped: reviewCountCapped,
            reported_total: reportedTotal
          };
        })()
      );
    }
    return cache.get(key);
  }

  function stars(rating) {
    const rounded = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    return '★★★★★'.slice(0, rounded) + '☆☆☆☆☆'.slice(0, 5 - rounded);
  }

  function toText(value) {
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (Array.isArray(value)) {
      const first = value.find((item) => toText(item));
      return first ? toText(first) : '';
    }
    if (value && typeof value === 'object') {
      return (
        toText(value.text)
        || toText(value.content)
        || toText(value.body)
        || toText(value.comment)
        || toText(value.value)
        || toText(value.name)
        || ''
      );
    }
    return '';
  }

  function getPrimaryAttribute(attributes) {
    if (!Array.isArray(attributes) || attributes.length === 0) return null;
    const raw = attributes[0];
    if (!raw || typeof raw !== 'object') return null;

    const label = toText(raw.label || raw.name);
    const options = Array.isArray(raw.options) ? raw.options.map((option) => toText(option)).filter(Boolean) : [];
    const value = Number(raw.value || 0);
    if (!label || options.length === 0 || Number.isNaN(value)) return null;

    return {
      label,
      options,
      value
    };
  }

  function buildAttributeMarkup(attribute) {
    if (!attribute) return '';
    const percent = Math.max(0, Math.min(100, ((attribute.value - 1) / 4) * 100));
    const midpoint = Math.floor((attribute.options.length - 1) / 2);
    return `
      <div class="prl-attribute">
        <p class="prl-attribute__label">${escapeHtml(attribute.label)}</p>
        <div class="prl-attribute__track">
          <span class="prl-attribute__line" aria-hidden="true"></span>
          <span class="prl-attribute__thumb" style="left:${percent}%;"></span>
        </div>
        <div class="prl-attribute__scale">
          <span>${escapeHtml(attribute.options[0] || '')}</span>
          <span>${escapeHtml(attribute.options[midpoint] || '')}</span>
          <span>${escapeHtml(attribute.options[attribute.options.length - 1] || '')}</span>
        </div>
      </div>
    `;
  }

  function hydrateSummary(node, data) {
    const average = Number(data.average_rating || 0);
    const count = Number(data.review_count || 0);
    const cappedPlus = Boolean(data.review_count_capped);
    const averageNode = node.querySelector('[data-prl-average]');
    const countNode = node.querySelector('[data-prl-count]');

    if (averageNode) averageNode.textContent = average.toFixed(1);
    if (countNode) countNode.textContent = cappedPlus ? `${REVIEWS_FETCH_CAP}+` : String(count);

    const ratingRoot = node.matches?.('[data-prl-rating]') ? node : node.closest('[data-prl-rating]');
    if (ratingRoot) {
      const aria = cappedPlus
        ? `${average.toFixed(1)} out of 5 stars, based on ${REVIEWS_FETCH_CAP}+ reviews`
        : `${average.toFixed(1)} out of 5 stars from ${count} review${count === 1 ? '' : 's'}`;
      ratingRoot.setAttribute('aria-label', aria);
    }
    const starsWrap = node.querySelector('.prl-rating__stars');
    if (starsWrap && starsWrap.querySelector('.prl-rating__stars-fill')) {
      const percent = Math.max(0, Math.min(100, (average / 5) * 100));
      starsWrap.style.setProperty('--rating-percent', `${percent}%`);
    } else if (starsWrap) {
      starsWrap.textContent = stars(average);
    }

    const summaryContainer = node.querySelector('[data-prl-summary]');
    if (summaryContainer) {
      const firstReview = Array.isArray(data.reviews) && data.reviews.length > 0 ? normalizeReview(data.reviews[0]) : null;
      const attribute = firstReview ? getPrimaryAttribute(firstReview.attributes) : null;
      let attributeNode = summaryContainer.querySelector('[data-prl-summary-attribute]');

      if (attribute) {
        if (!attributeNode) {
          attributeNode = document.createElement('div');
          attributeNode.dataset.prlSummaryAttribute = '';
          summaryContainer.appendChild(attributeNode);
        }
        attributeNode.innerHTML = buildAttributeMarkup(attribute);
      } else if (attributeNode) {
        attributeNode.remove();
      }
    }
  }

  function normalizeReview(review) {
    return {
      author: toText((review.author && review.author.name) || review.reviewer || review.author_name) || 'Verified customer',
      rating: Number(review.rating || review.rating_value || 0),
      title: toText(review.title || ''),
      body: toText(review.comments || review.body || review.content || review.review || ''),
      date: toText(review.date_created || review.date || review.created_at || review.created || ''),
      verified: Number(review.verified || 0) === 1,
      incentivized: Array.isArray(review.tags)
        && review.tags.some((tag) => String(tag).toLowerCase() === 'incentivized'),
      timeAgo: toText(review.time_ago || ''),
      attributes: Array.isArray(review.attributes) ? review.attributes : [],
      raw: review
    };
  }

  function formatDate(date) {
    if (!date) return '';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return date;
    return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  /** Align with Polaris: match variant SKU / variant id only — not Shopify product.id (duplicated PDP bleed). */
  function collectReviewSkus(review) {
    const pi = review.product_info;
    const prod = review.product;
    const candidates = [
      review.sku,
      review.product_sku,
      review.variant_sku,
      review.variant_id,
      review.shopify_variant_id,
      pi && pi.sku,
      pi && pi.product_sku,
      pi && pi.variant_id,
      pi && pi.shopify_variant_id,
      prod && prod.sku,
      prod && prod.variant_id
    ];
    return candidates.map(normalizeSku).filter(Boolean);
  }

  function dedupeReviews(reviews) {
    const seen = new Set();
    const result = [];
    for (const r of reviews) {
      const id = r.id ?? r.review_id ?? r.uuid;
      let key;
      if (id != null && id !== '') {
        key = `id:${String(id)}`;
      } else {
        const author = toText((r.author && r.author.name) || r.reviewer || r.author_name);
        const snippet = String(r.comments || r.body || r.content || '').slice(0, 120);
        key = `fp:${normalizeSku(author)}|${r.rating}|${r.date_created || r.date || ''}|${snippet}`;
      }
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(r);
    }
    return result;
  }

  function filterReviewsForProduct(node, reviews) {
    const skuTokens = getProductSkuTokens(node);
    if (skuTokens.length === 0) return [];

    const skuSet = new Set(skuTokens);

    return reviews.filter((review) => {
      const reviewSkus = collectReviewSkus(review);
      return reviewSkus.some((sku) => skuSet.has(sku));
    });
  }

  function computeSummaryFromReviews(reviews) {
    const count = reviews.length;
    if (count === 0) return { average_rating: 0, review_count: 0 };
    const total = reviews.reduce((sum, review) => sum + (Number(review.rating || 0) || 0), 0);
    return {
      average_rating: total / count,
      review_count: count
    };
  }

  function renderReviewsList(node, reviews) {
    const listNode = node.querySelector('[data-prl-list]');
    const stateNode = node.querySelector('[data-prl-state]');
    const loadMoreNode = node.querySelector('[data-prl-load-more]');
    if (!listNode || !stateNode) return;

    const perPage = Number(node.dataset.prlPerPage || 5);
    const all = reviews
      .map((r) => {
        try {
          return normalizeReview(r);
        } catch (_e) {
          return null;
        }
      })
      .filter(Boolean);
    let shown = 0;

    if (all.length === 0) {
      stateNode.textContent = 'No reviews yet for this product.';
      listNode.hidden = true;
      listNode.innerHTML = '';
      if (loadMoreNode) loadMoreNode.hidden = true;
      return;
    }

    stateNode.hidden = true;
    listNode.hidden = false;
    listNode.innerHTML = '';

    function appendSlice() {
      const next = all.slice(shown, shown + perPage);
      const markup = next.map((review) => `
        <li class="prl-review">
          <div class="prl-review__head">
            <div class="prl-review__identity">
              <span class="prl-review__author">${escapeHtml(review.author)}</span>
              ${review.verified ? '<div class="prl-review__verified"><span class="prl-review__icon">✔</span>Verified Customer</div>' : ''}
            </div>
          </div>
          <div class="prl-review__rating" aria-label="${review.rating} out of 5 stars">${stars(review.rating)}</div>
          ${review.title ? `<h3 class="prl-review__title">${escapeHtml(review.title)}</h3>` : ''}
          ${review.body ? `<p class="prl-review__body">${escapeHtml(review.body)}</p>` : ''}
          ${renderAttribute(review)}
          <div class="prl-review__footer">
            <div class="prl-review__footer-left">
              ${review.incentivized ? '<div class="prl-review__incentivized"><span class="prl-review__icon">i</span>Incentivized</div>' : ''}
            </div>
            <span class="prl-review__date">${escapeHtml(review.timeAgo || formatDate(review.date))}</span>
          </div>
        </li>
      `).join('');
      listNode.insertAdjacentHTML('beforeend', markup);
      shown += next.length;
      if (loadMoreNode) loadMoreNode.hidden = shown >= all.length;
    }

    if (loadMoreNode) {
      if (!loadMoreNode.dataset.prlBound) {
        loadMoreNode.addEventListener('click', appendSlice, { once: false });
        loadMoreNode.dataset.prlBound = 'true';
      }
      loadMoreNode.hidden = all.length <= perPage;
    }
    appendSlice();
  }

  function renderAttribute(review) {
    const attribute = getPrimaryAttribute(review.attributes);
    return buildAttributeMarkup(attribute);
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  async function hydrateReviewsBlock(node) {
    const stateNode = node.querySelector('[data-prl-state]');
    try {
      const raw = await getData(node);
      const allReviews = Array.isArray(raw.reviews) ? raw.reviews : (Array.isArray(raw.data) ? raw.data : []);
      const deduped = dedupeReviews(allReviews);
      const productReviews = filterReviewsForProduct(node, deduped);
      const productSummary = computeSummaryFromReviews(productReviews);
      const filteredCount = productSummary.review_count;
      const cappedFromApi = Boolean(raw.review_count_capped);
      const summaryData = {
        average_rating: productSummary.average_rating,
        review_count: filteredCount,
        review_count_capped: cappedFromApi && filteredCount >= REVIEWS_FETCH_CAP,
        reviews: productReviews
      };

      summaryNodes().forEach((summary) => {
        if (buildKey(summary) === buildKey(node)) {
          hydrateSummary(summary, summaryData);
        }
      });

      hydrateSummary(node, summaryData);

      renderReviewsList(node, productReviews);
    } catch (_error) {
      if (stateNode) stateNode.textContent = 'Reviews are temporarily unavailable.';
    }
  }

  function bootSummaryOnly() {}

  function bootReviewsBlocks() {
    const blocks = reviewsNodes();
    if (blocks.length === 0) return;

    if (!('IntersectionObserver' in window)) {
      blocks.forEach((block) => hydrateReviewsBlock(block));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        hydrateReviewsBlock(entry.target);
      });
    }, { rootMargin: '200px 0px' });

    blocks.forEach((block) => observer.observe(block));
  }

  document.addEventListener('DOMContentLoaded', () => {
    bootSummaryOnly();
    bootReviewsBlocks();
  }, { once: true });
})();